import React, { useState, useEffect, useRef, useCallback } from 'react';
import Axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import Constant from '../Constant';
import { getAccessToken } from '../../hooks/AccessToken';
import './AnalyticsDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ── Helpers ──────────────────────────────────────────────────

/**
 * Format seconds into human-readable duration
 */
function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

/**
 * Format large numbers with locale separators
 */
function formatNumber(num) {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString();
}

/**
 * Format date string to short label (e.g., "Jul 9")
 */
function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Get today's date in YYYY-MM-DD format
 */
function getTodayString() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get date N days ago in YYYY-MM-DD format
 */
function getDaysAgoString(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}


// ── KPI Card Config ──────────────────────────────────────────

const KPI_CONFIG = [
  { key: 'sessions', label: 'Sessions', icon: 'fa fa-signal', color: '#4e73df', format: formatNumber, desc: 'Total number of times the app or website was opened.' },
  { key: 'activeUsers', label: 'Active Users', icon: 'fa fa-users', color: '#1cc88a', format: formatNumber, desc: 'Number of unique people (devices) who visited.' },
  { key: 'pageViews', label: 'Page Views', icon: 'fa fa-eye', color: '#f6c23e', format: formatNumber, desc: 'Total number of app screens or web pages viewed.' },
  { key: 'avgSessionDuration', label: 'Avg Dur.', icon: 'fa fa-clock-o', color: '#36b9cc', format: formatDuration, desc: 'Average time a user spends in the app before closing it.' },
  { key: 'bounceRate', label: 'Bounce Rate', icon: 'fa fa-sign-out', color: '#e74a3b', format: (v) => `${(v || 0).toFixed(1)}%`, desc: 'Percentage of sessions with only one screen view and no other interactions.' },
  { key: 'newUsers', label: 'New Users', icon: 'fa fa-user-plus', color: '#6f42c1', format: formatNumber, desc: 'People who opened the app or website for the first time.' },
];

// ── Chart Colors ─────────────────────────────────────────────

const TRAFFIC_COLORS = [
  '#4e73df', '#1cc88a', '#f6c23e', '#e74a3b', '#36b9cc',
  '#6f42c1', '#fd7e14', '#20c997', '#6610f2', '#e83e8c',
];


// ── Main Component ───────────────────────────────────────────

const AnalyticsDashboard = () => {
  // State
  const [dateRange, setDateRange] = useState('30d');
  const [platform, setPlatform] = useState('all');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStart, setCustomStart] = useState(getDaysAgoString(30));
  const [customEnd, setCustomEnd] = useState(getTodayString());

  const [overview, setOverview] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [pages, setPages] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pickerRef = useRef(null);

  // ── API Calls ────────────────────────────────────────────

  const fetchAllData = useCallback(async (range, plat) => {
    setLoading(true);
    setError(null);

    const token = getAccessToken();
    const headers = { 'Content-Type': 'application/json', token };

    const params = `dateRange=${range}&platform=${plat}`;

    try {
      const [overviewRes, trafficRes, pagesRes, timeSeriesRes] = await Promise.all([
        Axios.get(`${Constant.apiBasePath}analytics/overview?${params}`, { headers }),
        Axios.get(`${Constant.apiBasePath}analytics/traffic?${params}`, { headers }),
        Axios.get(`${Constant.apiBasePath}analytics/pages?${params}&limit=10`, { headers }),
        Axios.get(`${Constant.apiBasePath}analytics/users-over-time?${params}`, { headers }),
      ]);

      if (overviewRes.data.status === Constant.statusSuccess) {
        setOverview(overviewRes.data.data);
      }
      if (trafficRes.data.status === Constant.statusSuccess) {
        setTraffic(trafficRes.data.data);
      }
      if (pagesRes.data.status === Constant.statusSuccess) {
        setPages(pagesRes.data.data);
      }
      if (timeSeriesRes.data.status === Constant.statusSuccess) {
        setTimeSeries(timeSeriesRes.data.data);
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(err.response?.data?.message || 'Failed to fetch analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Effects ──────────────────────────────────────────────

  useEffect(() => {
    fetchAllData(dateRange, platform);
  }, [dateRange, platform, fetchAllData]);

  // Close custom picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowCustomPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Date Range Handlers ──────────────────────────────────

  const handlePresetClick = (preset) => {
    setShowCustomPicker(false);
    setDateRange(preset);
  };

  const handleCustomApply = () => {
    if (customStart && customEnd) {
      setShowCustomPicker(false);
      setDateRange(`${customStart}_${customEnd}`);
    }
  };

  const isPresetActive = (preset) => dateRange === preset;
  const isCustomActive = dateRange.includes('_');

  // ── Chart Data ───────────────────────────────────────────

  const lineChartData = timeSeries ? {
    labels: timeSeries.map(d => formatDateLabel(d.date)),
    datasets: [
      {
        label: 'Active Users',
        data: timeSeries.map(d => d.users),
        borderColor: '#4e73df',
        backgroundColor: 'rgba(78, 115, 223, 0.08)',
        borderWidth: 2.5,
        pointRadius: timeSeries.length > 30 ? 0 : 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#4e73df',
        tension: 0.3,
        fill: true,
      },
      {
        label: 'Sessions',
        data: timeSeries.map(d => d.sessions),
        borderColor: '#1cc88a',
        backgroundColor: 'rgba(28, 200, 138, 0.05)',
        borderWidth: 2,
        pointRadius: timeSeries.length > 30 ? 0 : 3,
        pointHoverRadius: 5,
        pointBackgroundColor: '#1cc88a',
        tension: 0.3,
        fill: true,
        borderDash: [5, 5],
      },
    ],
  } : null;

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: { size: 12, weight: '600' },
        },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        boxPadding: 4,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 11 },
          color: '#94a3b8',
          maxTicksLimit: 12,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#f1f5f9' },
        ticks: {
          font: { size: 11 },
          color: '#94a3b8',
          padding: 8,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
  };

  const doughnutChartData = traffic ? {
    labels: traffic.map(t => t.source),
    datasets: [{
      data: traffic.map(t => t.sessions),
      backgroundColor: TRAFFIC_COLORS.slice(0, traffic.length),
      borderWidth: 2,
      borderColor: '#fff',
      hoverOffset: 8,
    }],
  } : null;

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 14,
          font: { size: 12, weight: '500' },
          generateLabels: (chart) => {
            const data = chart.data;
            if (!data.labels || !data.datasets) return [];
            return data.labels.map((label, i) => ({
              text: `${label} (${formatNumber(data.datasets[0].data[i])})`,
              fillStyle: data.datasets[0].backgroundColor[i],
              strokeStyle: '#fff',
              hidden: false,
              index: i,
              pointStyle: 'circle',
            }));
          },
        },
      },
      tooltip: {
        backgroundColor: '#1e293b',
        titleFont: { size: 13, weight: '600' },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${formatNumber(ctx.raw)} sessions (${pct}%)`;
          },
        },
      },
    },
    cutout: '65%',
  };

  // ── Render ───────────────────────────────────────────────

  return (
    <div className="content-page">
      <div className="content analytics-dashboard">
        {/* Header */}
        <div className="page-header">
          <h4 className="page-title-heading">
            <i className="fa fa-bar-chart" style={{ marginRight: 10, color: '#4e73df' }}></i>
            Analytics Dashboard
          </h4>

          <div className="header-controls">
            {/* Platform Toggle */}
            <div className="platform-toggle">
              {[
                { key: 'all', label: 'All', icon: 'fa fa-globe' },
                { key: 'web', label: 'Web', icon: 'fa fa-desktop' },
                { key: 'app', label: 'App', icon: 'fa fa-mobile' },
              ].map(opt => (
                <button
                  key={opt.key}
                  className={`platform-btn ${platform === opt.key ? 'active' : ''}`}
                  onClick={() => setPlatform(opt.key)}
                >
                  <i className={opt.icon} style={{ marginRight: 5 }}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          <div className="date-controls-wrapper" ref={pickerRef}>
            <div className="analytics-date-controls">
              {['7d', '30d', '90d'].map(preset => (
                <button
                  key={preset}
                  className={`date-btn ${isPresetActive(preset) ? 'active' : ''}`}
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset === '7d' ? 'Last 7 Days' : preset === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
                </button>
              ))}
              <button
                className={`date-btn ${isCustomActive ? 'active' : ''}`}
                onClick={() => setShowCustomPicker(!showCustomPicker)}
              >
                <i className="fa fa-calendar" style={{ marginRight: 6 }}></i>
                Custom
              </button>
            </div>

            {showCustomPicker && (
              <div className="custom-date-popup">
                <label>Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  max={customEnd}
                  onChange={(e) => setCustomStart(e.target.value)}
                />
                <label>End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  min={customStart}
                  max={getTodayString()}
                  onChange={(e) => setCustomEnd(e.target.value)}
                />
                <div className="popup-actions">
                  <button className="btn-cancel" onClick={() => setShowCustomPicker(false)}>Cancel</button>
                  <button className="btn-apply" onClick={handleCustomApply}>Apply</button>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Error State */}
        {error && !loading && (
          <div className="analytics-error">
            <i className="fa fa-exclamation-triangle"></i>
            <h5>Unable to Load Analytics</h5>
            <p>{error}</p>
            <button className="btn-retry" onClick={() => fetchAllData(dateRange)}>
              <i className="fa fa-refresh" style={{ marginRight: 6 }}></i>
              Retry
            </button>
          </div>
        )}

        {/* KPI Cards */}
        {!error && (
          <div className="analytics-kpi-grid">
            {KPI_CONFIG.map((kpi) => (
              <div className="analytics-kpi-card" key={kpi.key}>
                <div className="accent-bar" style={{ background: kpi.color }}></div>
                <div className="kpi-body">
                  <div className="kpi-info">
                    <div className="kpi-label">
                      {kpi.label}
                      <div className="tooltip-container">
                        <i className="fa fa-info-circle"></i>
                        <span className="tooltip-text">{kpi.desc}</span>
                      </div>
                    </div>
                    {loading ? (
                      <div className="skeleton-pulse" style={{ width: 80, height: 28 }}></div>
                    ) : (
                      <div className="kpi-value">
                        {kpi.format(overview ? overview[kpi.key] : 0)}
                      </div>
                    )}
                  </div>
                  <div className="kpi-icon" style={{ background: `${kpi.color}1a` }}>
                    <i className={kpi.icon} style={{ color: kpi.color }}></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Charts Row */}
        {!error && (
          <div className="analytics-charts-row">
            {/* Line Chart — Users Over Time */}
            <div className="analytics-chart-card">
              <h5>
                <i className="fa fa-line-chart" style={{ marginRight: 8, color: '#4e73df' }}></i>
                Users &amp; Sessions Over Time
                <div className="tooltip-container">
                  <i className="fa fa-info-circle"></i>
                  <span className="tooltip-text">Shows daily traffic trends. Sessions indicate total visits, while Active Users show unique people.</span>
                </div>
              </h5>
              <div className="chart-container line-chart">
                {loading ? (
                  <div className="skeleton-pulse chart-skeleton"></div>
                ) : timeSeries && timeSeries.length > 0 ? (
                  <Line data={lineChartData} options={lineChartOptions} />
                ) : (
                  <div className="analytics-empty">
                    <i className="fa fa-line-chart"></i>
                    <p>No time series data available for this period</p>
                  </div>
                )}
              </div>
            </div>

            {/* Doughnut Chart — Traffic Sources */}
            <div className="analytics-chart-card">
              <h5>
                <i className="fa fa-pie-chart" style={{ marginRight: 8, color: '#1cc88a' }}></i>
                Traffic Sources
                <div className="tooltip-container">
                  <i className="fa fa-info-circle"></i>
                  <span className="tooltip-text">Where your users came from (e.g., Organic Search, Direct link, Social Media).</span>
                </div>
              </h5>
              <div className="chart-container doughnut-chart">
                {loading ? (
                  <div className="skeleton-pulse chart-skeleton" style={{ width: '100%' }}></div>
                ) : traffic && traffic.length > 0 ? (
                  <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
                ) : (
                  <div className="analytics-empty">
                    <i className="fa fa-pie-chart"></i>
                    <p>No traffic source data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Top Pages Table */}
        {!error && (
          <div className="analytics-table-card">
            <h5>
              <i className="fa fa-file-text-o" style={{ marginRight: 8, color: '#f6c23e' }}></i>
              Top Pages &amp; Screens
              <div className="tooltip-container">
                <i className="fa fa-info-circle"></i>
                <span className="tooltip-text">The most frequently viewed app screens or website pages. 'MainActivity' is the default Android screen unless custom screen names are set in the app.</span>
              </div>
            </h5>
            {loading ? (
              <div>
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="skeleton-pulse table-skeleton"></div>
                ))}
              </div>
            ) : pages && pages.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>Page / Screen</th>
                    <th>Platform</th>
                    <th>Views</th>
                    <th>Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((page, idx) => (
                    <tr key={idx}>
                      <td>
                        <span className="rank-badge">{idx + 1}</span>
                      </td>
                      <td>
                        <div>{page.pageTitle || '(not set)'}</div>
                        {page.pagePath && page.pagePath !== page.pageTitle && (
                          <div className="page-path">{page.pagePath}</div>
                        )}
                      </td>
                      <td>
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          background: page.platform === 'Android' ? '#e6f4ea' : page.platform === 'iOS' ? '#f1f3f4' : '#e8f0fe',
                          color: page.platform === 'Android' ? '#137333' : page.platform === 'iOS' ? '#3c4043' : '#1a73e8',
                          borderRadius: '10px',
                          fontWeight: '600'
                        }}>
                          {page.platform || 'Web'}
                        </span>
                      </td>
                      <td>{formatNumber(page.views)}</td>
                      <td>{formatNumber(page.sessions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="analytics-empty">
                <i className="fa fa-file-text-o"></i>
                <p>No page data available for this period</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
